// src/context/SeasonContext.js
import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { collection, getDocs, getDoc, doc } from "firebase/firestore";
import { db } from "../lib/firebase";

export const SeasonContext = createContext({
  availableSeasons: [],
  selectedSeasonID: "S1",
  setSelectedSeasonID: () => {},
  currentSeasonID: "S1",
  isCurrentSeason: true,
});

/**
 * Parses the leagueID from the URL pathname.
 * All league pages follow the pattern: /{page}/{leagueID}[/...]
 * e.g. /statistics/abc123, /leaderboard/abc123
 */
function useLeagueIDFromURL() {
  const location = useLocation();
  return useMemo(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    // League pages always have leagueID as the second segment
    // Non-league pages (/home, /login, /faq, /create, /join) only have one segment
    return parts.length >= 2 ? parts[1] : null;
  }, [location.pathname]);
}

export const SeasonProvider = ({ children }) => {
  const leagueID = useLeagueIDFromURL();

  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [currentSeasonID, setCurrentSeasonID] = useState("S1");
  const [selectedSeasonID, setSelectedSeasonID] = useState("S1");

  // Fetch seasons whenever the leagueID changes
  useEffect(() => {
    if (!leagueID) return;

    let cancelled = false;

    (async () => {
      try {
        // 1. Get the current season from the league root doc
        const leagueSnap = await getDoc(doc(db, "leagues", leagueID));
        if (!leagueSnap.exists() || cancelled) return;

        const liveSeasonID = leagueSnap.data().currentSeasonID || "S1";

        // 2. Enumerate all seasons under leagues/{leagueID}/seasons/
        const seasonsSnap = await getDocs(
          collection(db, "leagues", leagueID, "seasons")
        );
        if (cancelled) return;

        // Sort S1, S2, S3 ... numerically
        const seasons = seasonsSnap.docs
          .map((d) => d.id)
          .filter((id) => /^S\d+$/.test(id))
          .sort((a, b) => {
            const aNum = parseInt(a.replace("S", ""), 10);
            const bNum = parseInt(b.replace("S", ""), 10);
            return aNum - bNum;
          });

        setAvailableSeasons(seasons);
        setCurrentSeasonID(liveSeasonID);
        // Reset to current season whenever league changes
        setSelectedSeasonID(liveSeasonID);
      } catch (err) {
        console.warn("[SeasonContext] failed to fetch seasons:", err);
      }
    })();

    return () => { cancelled = true; };
  }, [leagueID]);

  const isCurrentSeason = selectedSeasonID === currentSeasonID;

  return (
    <SeasonContext.Provider
      value={{
        availableSeasons,
        selectedSeasonID,
        setSelectedSeasonID,
        currentSeasonID,
        isCurrentSeason,
      }}
    >
      {children}
    </SeasonContext.Provider>
  );
};

export function useSeason() {
  return useContext(SeasonContext);
}
